import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as PARTS from "../../constants/config/parts";
import Checkbox from "./Checkbox";
import Parts from "./Parts";
import Columns from "./Columns";
import Filters from "./Filters";
import Data from "./Data";
import AlertModal from "../AlertModal";
import { updateWidth } from "../../actions/table";
import NoData from "./Data/NoData";
import { select } from "../../actions/parts";
import AddMyAnalogModal from "../AddMyAnalogModal";
import Scroll from "../Table/Scroll/Scroll";
import localization from "../../data/config";
import "./style.css";
import MediaQuery from "react-responsive";
import breakpoints from "../../constants/breakpoints";

/**
 * Custom hook to handle scroll-related states
 */
const useScroll = (theadRef) => {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (theadRef.current) {
        const { top } = theadRef.current.getBoundingClientRect();
        setIsSticky(top <= 57);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [theadRef]);

  return { isSticky };
};

/**
* @name Table
* @function
* @description Displays PST Table
* @returns {JSX}
* @param {*} props
*/
const Table = () => {
  const tableRef = useRef(null);
  const tableTagRef = useRef(null);
  const theadRef = useRef(null);
  const dispatch = useDispatch();
  const { isSticky } = useScroll(theadRef);
  const [selectAll, setSelectAll] = useState(false);
  const [sortStatus, setSortStatus] = useState(null);
  const [leftScroll, setLeftScroll] = useState(null);
  const { parameters, label, count, parts, width, defaultPlaceholder, allData } = useSelector((state) => ({
    parameters: state.filters.parameters,
    label: state.labels,
    count: state.parts.count,
    parts: state.parts.data,
    width: state.table.width,
    defaultPlaceholder: state.labels.inputs.defaultPlaceholder,
    allData: state.allData,
  }));

    // useEffect used to update the table width
    useEffect(() => {
      let tempWidth = 0;
      const thElements = Array.from(theadRef.current.children[0].children);
      thElements.slice(2).forEach((thElement) => {
        tempWidth += thElement.offsetWidth;
      });
      dispatch(updateWidth(tempWidth));
    }, [parameters, width]);
    
  /**
   * @name handleSelectAll
   * @function
   * @description Handle's select all checkbox
   */
  const handleSelectAll = () => {
    dispatch(select(PARTS.SELECT_ALL, parts));
    setSelectAll(!selectAll); 
  };

  return (
    <>
      <div className="pst_table" ref={tableRef}>
        <table ref={tableTagRef}>
          <thead role="row" ref={theadRef}>
            <tr className="table__header">
              <MediaQuery minDeviceWidth={breakpoints.tablet.max + 1}>
                <th className="header-checkbox">
                  <Checkbox onClick={handleSelectAll} hasAriaLabel ariaLabelValue={ localization.selectAllLabel } checked={selectAll}/>
                </th>
              </MediaQuery>
              <Parts
                value={label.parts}
                count={count}
                className={leftScroll ? "part__scroll" : ""}
                sortStatus={sortStatus}
                setSortStatus={setSortStatus}
              />
              <Columns
                value={parameters}
                sortStatus={sortStatus}
                setSortStatus={setSortStatus}
              />
            </tr>
            <Filters
              value={parameters}
              label={label}
              defaultPlaceholder={defaultPlaceholder}
              className={leftScroll ? "part__scroll" : ""}
              verticalScrollClass={isSticky ? "filters__shadow" : ""}
            />
          </thead>
          <tbody>
            {parts.length > 0 && (
              <Data
                className={leftScroll ? "part__scroll" : ""}
                selectAll={selectAll}
                setSelectAll={setSelectAll}
              />
            )}
          </tbody>
        </table>
        <AlertModal />
        <AddMyAnalogModal />
      </div>
      {!parts.length && <NoData />}
      <MediaQuery minDeviceWidth={breakpoints.laptop.min}>
        <Scroll setLeftScroll={setLeftScroll} parameters={parameters} parts={parts} />
      </MediaQuery>
    </>
  );
};

Table.propTypes = {
  parameters: PropTypes.array,
  parts: PropTypes.array,
  tooltip: PropTypes.shape({
    coords: PropTypes.object,
    label: PropTypes.string,
    count: PropTypes.number,
  }),
  defaultPlaceholder: PropTypes.string,
};

export default Table;
